import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
  Theme,
} from "@material-ui/core"

import { useTranslation } from "react-i18next"

import { useAppContext } from "../../app-context/use-app-context"
import { useChatMutations } from "../hooks/use-chat-mutations"
import { useGetOrgMembershipsQuery } from "../../memberships/api"
import { ChatResponse } from "../api/chat-response"

type Props = {
  open: boolean
  orgId: string
  chat: ChatResponse
  onClose: () => void
  onUpdated?: (chatId: string) => void
}

const ManageParticipantsDialog = ({ open, orgId, chat, onClose, onUpdated }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const { t: common } = useTranslation("translation")

  const {
    appContext: { mainContextList },
  } = useAppContext()

  const currentUserId = mainContextList.find((ctx) => ctx.type === "user")?.id

  const { updateChat } = useChatMutations()

  const { data: activeMemberships = [] } = useGetOrgMembershipsQuery(orgId)

  const [search, setSearch] = useState("")
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const initialParticipantIds = useMemo(() => chat.participantIds ?? [], [chat])

  // Reset selection to the chat's current members whenever the dialog opens
  useEffect(() => {
    if (open) {
      setSelectedUserIds(initialParticipantIds)
      setSearch("")
    }
  }, [open, initialParticipantIds])

  const members = useMemo(
    () => activeMemberships.filter((m) => m.userId !== currentUserId),
    [activeMemberships, currentUserId]
  )

  const filteredMembers = useMemo(() => {
    const term = search.toLowerCase()

    return members.filter(
      (m) => `${m.firstname} ${m.lastname}`.toLowerCase().includes(term) || m.userEmail.toLowerCase().includes(term)
    )
  }, [members, search])

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((x) => x !== userId) : [...prev, userId]))
  }

  const hasChanges = useMemo(() => {
    if (initialParticipantIds.length !== selectedUserIds.length) return true

    const after = new Set(selectedUserIds)

    return initialParticipantIds.some((id) => !after.has(id))
  }, [initialParticipantIds, selectedUserIds])

  const canSave = hasChanges && selectedUserIds.length >= 2

  const handleClose = () => {
    onClose()
  }

  const handleSave = async () => {
    if (!canSave) return

    setLoading(true)

    try {
      await updateChat(chat.id, { participantIds: selectedUserIds })

      onUpdated?.(chat.id)
      handleClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("manageParticipantsDialog.title")}</DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label={t("searchPeopleLabel")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Typography variant="subtitle2" className={classes.sectionTitle}>
          {t("manageParticipantsDialog.membersSectionTitle", { count: selectedUserIds.length })}
        </Typography>

        <List className={classes.list}>
          {filteredMembers.map((member) => (
            <ListItem key={member.userId} button onClick={() => toggleUser(member.userId)}>
              <Checkbox checked={selectedUserIds.includes(member.userId)} />

              <ListItemText primary={`${member.firstname} ${member.lastname}`} secondary={member.userEmail} />
            </ListItem>
          ))}
        </List>

        {selectedUserIds.length < 3 && (
          <Typography variant="caption" color="error">
            {t("manageParticipantsDialog.minimumParticipantsMessage")}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{common("cancel")}</Button>

        <Button
          color="primary"
          variant="contained"
          disabled={!canSave || loading || selectedUserIds.length < 3}
          onClick={handleSave}
        >
          {common("save")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  list: {
    maxHeight: 320,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
}))

export default ManageParticipantsDialog
